import React, { FC } from 'react';
import { Typography } from '@mui/material';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../components/store/Store';
import SettingsInputContainer from '../../../components/SettingsInputContainer';
import TextInput from '../../../components/forms/TextInput';
import Container from '../../../components/Container';
import CheckboxInput from '../../../components/forms/CheckboxInput';
import { Site } from '../../../declarations/models/Site';

export const WebpageTrackingSettings: FC = () => {
  const { t } = useTranslation('components');
  const { state } = useStore();
  const isSuperAdmin = state.currentUser?.user?.is_super_admin;

  const {
    field: { value: enablePlausible, onChange: onChangeEnablePlausible },
  } = useController<Site, `content.settings.enablePlausible`>({
    name: `content.settings.enablePlausible`,
  });

  return (
    <>
      <SettingsInputContainer title={t(`Settings.WebpageSettings.WebpageTrackingSettings.plausible`)}>
        <Container fullWidth spaceBetween gap={2}>
          <Container column left fullWidth gap={2}>
            <Typography>{t(`Settings.WebpageSettings.WebpageTrackingSettings.plausibleDescription`)}</Typography>

            {enablePlausible && (
              <Typography>
                <strong>{t('Settings.WebpageSettings.WebpageTrackingSettings.plausibleEnabled')}</strong>
              </Typography>
            )}
            {!enablePlausible && (
              <Typography>{t('Settings.WebpageSettings.WebpageTrackingSettings.plausibleNotEnabled')}</Typography>
            )}

            {isSuperAdmin && (
              <div className='superadmin' style={{ backgroundColor: '#fbe6ff', padding: '1em', width: '100%' }}>
                <Container column left fullWidth>
                  <CheckboxInput
                    fullWidth={false}
                    path='content.settings.enablePlausible'
                    label={t('Settings.WebpageSettings.WebpageTrackingSettings.plausibleEnable')}
                  />
                  <TextInput
                    path='content.settings.plausibleWebsiteId'
                    defaultValue=''
                    label={t(`Settings.WebpageSettings.WebpageTrackingSettings.plausibleWebsiteId`)}
                  />
                  <TextInput
                    path='content.settings.plausibleScriptId'
                    defaultValue=''
                    label={t(`Settings.WebpageSettings.WebpageTrackingSettings.plausibleScriptId`)}
                  />
                  <TextInput
                    path='content.settings.plausibleAuth'
                    defaultValue=''
                    label={t(`Settings.WebpageSettings.WebpageTrackingSettings.plausibleAuth`)}
                    placeholder={t(`Settings.WebpageSettings.WebpageTrackingSettings.plausibleAuth`)}
                  />
                  <CheckboxInput
                    fullWidth={false}
                    path='content.settings.plausibleUniqueDomain'
                    label={t('Settings.WebpageSettings.WebpageTrackingSettings.plausibleUniqueDomain')}
                  />
                  <CheckboxInput
                    fullWidth={false}
                    path='content.settings.plausibleEnableLocalhostTracking'
                    label={t('Settings.WebpageSettings.WebpageTrackingSettings.plausibleEnableLocalhostTracking')}
                  />
                </Container>
              </div>
            )}
          </Container>
        </Container>
      </SettingsInputContainer>

      <SettingsInputContainer title={t(`Settings.WebpageSettings.WebpageTrackingSettings.google`)}>
        <Container fullWidth spaceBetween gap={2}>
          <Container column left fullWidth>
            <Typography variant='subtitle2'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.googleGtagKey`)}
            </Typography>
            <TextInput path='content.settings.googleGtagKey' defaultValue='' label='' placeholder='G-XXXXXX' />
          </Container>
          <Container column left fullWidth>
            <Typography variant='subtitle2'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.googleTagmanagerKey`)}
            </Typography>
            <TextInput path='content.settings.googleTagmanagerKey' defaultValue='' label='' placeholder='Key' />
          </Container>
        </Container>

        <Container fullWidth spaceBetween gap={2}>
          <Container column left fullWidth>
            <Typography variant='subtitle2'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.googleSiteVerification`)}
            </Typography>
            <TextInput path='content.settings.googleSiteVerification' defaultValue='' label='' placeholder='UID' />
          </Container>
          <Container column left fullWidth>
            <Typography variant='subtitle2'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.googleMapsKey`)}
            </Typography>
            <TextInput
              path='content.settings.googleMapsKey'
              defaultValue=''
              label=''
              placeholder={t(`Settings.WebpageSettings.WebpageTrackingSettings.googleMapsKeyPlaceholder`)}
            />
          </Container>
        </Container>
        <Container fullWidth spaceBetween gap={2} column left>
          <CheckboxInput
            fullWidth={false}
            path='content.settings.enableStructuredGoogleBreadcrumbs'
            label={t('Settings.WebpageSettings.WebpageTrackingSettings.enableStructuredGoogleBreadcrumbs')}
          />
          <CheckboxInput
            fullWidth={false}
            path='content.settings.enableStructuredGoogleEvents'
            label={t('Settings.WebpageSettings.WebpageTrackingSettings.enableStructuredGoogleEvents')}
          />
        </Container>
      </SettingsInputContainer>

      <SettingsInputContainer title={t(`Settings.WebpageSettings.WebpageTrackingSettings.facebook`)}>
        <Container fullWidth spaceBetween gap={2}>
          <Container column left fullWidth>
            <Typography variant='subtitle2'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.facebookPixcelCode`)}
            </Typography>
            <TextInput
              path='content.settings.facebookPixcelCode'
              defaultValue=''
              label=''
              placeholder={t(`Settings.WebpageSettings.WebpageTrackingSettings.facebookPixcelCodePlaceholder`)}
            />
            <Typography variant='caption'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.facebookPixcelCodeCaption`)}
            </Typography>
          </Container>
          <Container column left fullWidth>
            <Typography variant='subtitle2'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.facebookDomainVerification`)}
            </Typography>
            <TextInput
              path='content.settings.facebookDomainVerification'
              defaultValue=''
              label=''
              placeholder={t(`Settings.WebpageSettings.WebpageTrackingSettings.facebookDomainVerificationPlaceholder`)}
            />
            <Typography variant='caption'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.facebookDomainVerificationCaption`)}
            </Typography>
          </Container>
        </Container>
      </SettingsInputContainer>
    </>
  );
};

export default WebpageTrackingSettings;
